import {
  getSJVCCampusByZip as _sjvcGetCampusByZip,
  getSJVCProgramsForCampus as _sjvcGetProgramsForCampus,
} from './sjvcData'
import {
  getCarringtonCampusByZip as _carringtonGetCampusByZip,
  getCarringtonProgramsForCampus as _carringtonGetProgramsForCampus,
  getCarringtonCampusDirectory as _carringtonGetCampusDirectory,
  CARRINGTON_PROGRAMS as _carringtonPrograms,
} from './carringtonData'

export interface UniversityProgram {
  name: string
  category: string
  degreeLevel?: string
  slug?: string
  // Advertiser program ID (mProgram value posted on lead submit) when the
  // partner provides a numeric program catalog.
  programId?: number
  // Advertiser program string value (mProgram value posted on lead submit)
  // when the partner requires a specific string instead of a numeric ID.
  mProgramValue?: string
}

export interface UniversityFeature {
  icon: string
  label: string
  description: string
  superscript?: string
}

export interface UniversityTestimonial {
  quote: string
  name: string
  program: string
}

export interface UniversityData {
  slug: string
  name: string
  shortName: string
  tagline: string
  heroTagline: string
  mission: string
  primaryColor: string
  secondaryColor: string
  ctaColor: string
  logoText: string
  logoUrl?: string
  heroImageUrl?: string
  phoneNumber?: string
  classesStartDate?: string
  programs: UniversityProgram[]
  degreeLevels: string[]
  features: UniversityFeature[]
  testimonial?: UniversityTestimonial
  mobileSection?: {
    heading: string
    description: string
  }
  accreditation: string
  founded: string
  type: string
  tcpaText?: string
  // Optional second TCPA disclosure (e.g. SMS opt-in shown as a separate checkbox)
  tcpaTextSms?: string
  // Required advertiser/lead routing identifiers (per-partner config)
  mPubID?: string
  mPubCampaignID?: string
  mBrandID?: string
  // Optional override for the lead post endpoint
  leadPostUrl?: string
  // Campus-aware advertisers (e.g. SJVC): provide a zip→campus lookup and
  // a programs-for-campus resolver. When present, the form prepends a
  // step 0 that asks the user for their zip to determine campus + programs.
  campusZipLookup?: (zip: string) => { id: number; name: string } | null
  programsForCampus?: (campusId: number) => UniversityProgram[]
  // Optional landing-page content rendered on the first step of the
  // advertiser form to lift CTR. Includes a benefit-driven headline,
  // supporting paragraph, four feature blurbs, and a closing career
  // section with copy + image.
  // Optional hero background image, overlaid on the primary color band.
  heroBackgroundImage?: string
  formHeading?: string
  submitLabel?: string
  ctaTextColor?: string
  // When true, consent is given by clicking the submit button (per the
  // partner's eSIGN language) rather than a checkbox, so the TCPA text
  // renders as plain copy and never blocks submission.
  consentOnSubmit?: boolean
  // When true, the program step shows ONE dropdown listing every program
  // instead of the Degree Level → Area of Study → Program cascade. The
  // degree level and category are taken from the selected program.
  singleProgramSelect?: boolean
  landingContent?: {
    headline: string
    subheadline: string
    // Optional short bullet highlights rendered beneath the hero paragraph
    // to keep the copy scannable instead of one dense block of text.
    heroHighlights?: string[]
    features: { icon: string; title: string; description: string }[]
    stats?: { value: string; label: string }[]
    careerSection?: {
      headline: string
      body: string
      imageUrl: string
      imageAlt?: string
    }
    testimonial?: { quote: string; name: string; program: string }
    infoSections?: {
      eyebrow?: string
      heading: string
      body: string
      bullets?: string[]
    }[]
    finalCta?: { headline: string; body: string; buttonLabel?: string }
    // Optional community / lifestyle image rendered in its own section.
    communityImage?: {
      url: string
      alt?: string
      eyebrow?: string
      heading?: string
      body?: string
    }
    // Optional campus directory listing (campus name + programs offered).
    campusList?: { id: number; name: string; programs: string[] }[]
  }
}

export const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')

export const getProgramBySlug = (
  university: UniversityData,
  programSlug: string,
) => {
  return university.programs.find(
    (p) => (p.slug || slugify(p.name)) === programSlug,
  )
}

// UAGC and CTU use the SJVC-style campus flow (step 0 = zip lookup), but
// instead of a real zip→campus map every zip resolves to a single
// "Online Campus" and all of the university's programs are available.
const ONLINE_CAMPUS = { id: 1, name: 'Online Campus' }

const CRESTMONT_PROGRAMS: UniversityProgram[] = [
  {
    name: 'Associate of Arts in Business Administration',
    category: 'Business',
    degreeLevel: "Associate's",
    mProgramValue: 'Associate of Arts in Business Administration',
  },
  {
    name: "Bachelor's of Business Administration",
    category: 'Business',
    degreeLevel: "Bachelor's",
    mProgramValue: 'Bachelor of Business Administration',
  },
  {
    name: 'Bachelors of Business Administration in Internet Marketing and Entrepreneurship',
    category: 'Business',
    degreeLevel: "Bachelor's",
    mProgramValue:
      'Bachelor of Business Administration in Internet Marketing and Entrepreneurship',
  },
  {
    name: 'Bachelors of Engineering Information Technology - Networking and Systems Security',
    category: 'Information Technology',
    degreeLevel: "Bachelor's",
    mProgramValue:
      'Bachelor of Engineering Information Technology in Networking and Systems Security',
  },
  {
    name: "Bachelor's Information Technology in Project Management",
    category: 'Information Technology',
    degreeLevel: "Bachelor's",
    mProgramValue:
      'Bachelor of Engineering Information Technology in Project Management',
  },
]

const CTU_PROGRAMS: UniversityProgram[] = [
  { name: 'MBA', category: 'Business', degreeLevel: "Master's" },
  { name: 'Management', category: 'Business', degreeLevel: "Bachelor's" },
  { name: 'Accounting', category: 'Business', degreeLevel: "Bachelor's" },
  { name: 'Marketing', category: 'Business', degreeLevel: "Bachelor's" },
  { name: 'Project Management', category: 'Business', degreeLevel: "Master's" },
  { name: 'Computer Science', category: 'IT', degreeLevel: "Bachelor's" },
  { name: 'Cybersecurity', category: 'IT', degreeLevel: "Bachelor's" },
  { name: 'Software Engineering', category: 'IT', degreeLevel: "Bachelor's" },
  { name: 'Data Analytics', category: 'IT', degreeLevel: "Bachelor's" },
  { name: 'IT Management', category: 'IT', degreeLevel: "Master's" },
  {
    name: 'Healthcare Management',
    category: 'Healthcare',
    degreeLevel: "Master's",
  },
  {
    name: 'Health Informatics',
    category: 'Healthcare',
    degreeLevel: "Bachelor's",
  },
  {
    name: 'Criminal Justice',
    category: 'Criminal Justice',
    degreeLevel: "Bachelor's",
  },
  {
    name: 'Homeland Security',
    category: 'Criminal Justice',
    degreeLevel: "Bachelor's",
  },
  {
    name: 'Electrical Engineering',
    category: 'Engineering',
    degreeLevel: "Bachelor's",
  },
  {
    name: 'Computer Engineering',
    category: 'Engineering',
    degreeLevel: "Bachelor's",
  },
]

// UAGC program catalog (advertiser-provided Program IDs). Display names are
// the program subject; the degree (A.A./B.A./B.S.) is conveyed via the
// degreeLevel dropdown. programId is posted as mProgram on lead submit.
const UAGC_PROGRAMS: UniversityProgram[] = [
  // Associate's
  {
    name: 'Business',
    category: 'Business',
    degreeLevel: "Associate's",
    programId: 54516,
  },
  {
    name: 'Early Childhood Education',
    category: 'Education',
    degreeLevel: "Associate's",
    programId: 1639,
  },
  {
    name: 'Military Studies',
    category: 'General Studies',
    degreeLevel: "Associate's",
    programId: 54693,
  },
  {
    name: 'Organizational Management',
    category: 'Business',
    degreeLevel: "Associate's",
    programId: 54517,
  },
  // Bachelor's
  {
    name: 'Accounting',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 1610,
  },
  {
    name: 'Applied Behavioral Science',
    category: 'Psychology & Social Sciences',
    degreeLevel: "Bachelor's",
    programId: 1643,
  },
  {
    name: 'Business Administration',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 1608,
  },
  {
    name: 'Business Information Systems',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 1609,
  },
  {
    name: 'Business Leadership',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 1611,
  },
  {
    name: 'Early Childhood Development w/ Differentiated Instruction',
    category: 'Education',
    degreeLevel: "Bachelor's",
    programId: 1645,
  },
  {
    name: 'Early Childhood Education',
    category: 'Education',
    degreeLevel: "Bachelor's",
    programId: 1612,
  },
  {
    name: 'Early Childhood Education Administration',
    category: 'Education',
    degreeLevel: "Bachelor's",
    programId: 1613,
  },
  {
    name: 'Education Studies',
    category: 'Education',
    degreeLevel: "Bachelor's",
    programId: 1637,
  },
  {
    name: 'Finance',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 1614,
  },
  {
    name: 'Human Resources Management',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 1618,
  },
  {
    name: 'Information Technology',
    category: 'Information Technology',
    degreeLevel: "Bachelor's",
    programId: 1625,
  },
  {
    name: 'Instructional Design',
    category: 'Education',
    degreeLevel: "Bachelor's",
    programId: 1641,
  },
  {
    name: 'Marketing',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 1619,
  },
  {
    name: 'Organizational Management',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 1621,
  },
  {
    name: 'Project Management',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 1622,
  },
  {
    name: 'Psychology',
    category: 'Psychology & Social Sciences',
    degreeLevel: "Bachelor's",
    programId: 1671,
  },
  {
    name: 'Social and Criminal Justice',
    category: 'Criminal Justice',
    degreeLevel: "Bachelor's",
    programId: 1642,
  },
  {
    name: 'Sociology',
    category: 'Psychology & Social Sciences',
    degreeLevel: "Bachelor's",
    programId: 1672,
  },
  {
    name: 'Computer Software Technology',
    category: 'Information Technology',
    degreeLevel: "Bachelor's",
    programId: 1628,
  },
  {
    name: 'Cyber & Data Security Technology',
    category: 'Information Technology',
    degreeLevel: "Bachelor's",
    programId: 1640,
  },
]

const HCU_PROGRAMS: UniversityProgram[] = [
  // Master's
  {
    name: 'Apologetics',
    category: 'Religious Studies',
    degreeLevel: "Master's",
    programId: 67011,
  },
  {
    name: 'Christian Leadership – Practical Theology Track',
    category: 'Religious Studies',
    degreeLevel: "Master's",
    programId: 67014,
  },
  {
    name: 'Clinical Mental Health Counseling',
    category: 'Psychology & Counseling',
    degreeLevel: "Master's",
    programId: 67015,
  },
  {
    name: 'Human Services – Marriage and Family Track',
    category: 'Psychology & Counseling',
    degreeLevel: "Master's",
    programId: 67016,
  },
  {
    name: 'Human Services – Professional Life Coaching Track',
    category: 'Psychology & Counseling',
    degreeLevel: "Master's",
    programId: 67040,
  },
  {
    name: 'Marriage and Family Therapy',
    category: 'Psychology & Counseling',
    degreeLevel: "Master's",
    programId: 67018,
  },
  {
    name: 'Psychology – Applied Project Track',
    category: 'Psychology & Counseling',
    degreeLevel: "Master's",
    programId: 67041,
  },
  {
    name: 'Psychology – Teaching Track',
    category: 'Psychology & Counseling',
    degreeLevel: "Master's",
    programId: 67044,
  },
  {
    name: 'Psychology – Thesis Track',
    category: 'Psychology & Counseling',
    degreeLevel: "Master's",
    programId: 67042,
  },
  {
    name: 'Theological Studies',
    category: 'Religious Studies',
    degreeLevel: "Master's",
    programId: 67019,
  },
  {
    name: 'Professional MBA – Accounting and Finance',
    category: 'Business',
    degreeLevel: "Master's",
    programId: 67039,
  },
  {
    name: 'Professional MBA – Business Data Analytics',
    category: 'Business',
    degreeLevel: "Master's",
    programId: 67013,
  },
  {
    name: 'Professional MBA – Management',
    category: 'Business',
    degreeLevel: "Master's",
    programId: 67010,
  },
  {
    name: 'Master of Divinity – Biblical Languages Track',
    category: 'Religious Studies',
    degreeLevel: "Master's",
    programId: 67020,
  },
  {
    name: 'Master of Divinity – English Language Track',
    category: 'Religious Studies',
    degreeLevel: "Master's",
    programId: 67021,
  },
  {
    name: 'Curriculum and Instruction',
    category: 'Education',
    degreeLevel: "Master's",
    programId: 67022,
  },
  {
    name: 'Educational Administration',
    category: 'Education',
    degreeLevel: "Master's",
    programId: 67023,
  },
  {
    name: 'Professional School Counseling',
    category: 'Education',
    degreeLevel: "Master's",
    programId: 67045,
  },
  {
    name: 'Health Administration – Health Professions (PSM)',
    category: 'Healthcare',
    degreeLevel: "Master's",
    programId: 67017,
  },
  {
    name: 'Artificial Intelligence',
    category: 'Information Technology',
    degreeLevel: "Master's",
    programId: 67028,
  },
  {
    name: 'Criminal Justice',
    category: 'Criminal Justice',
    degreeLevel: "Master's",
    programId: 67012,
  },
  {
    name: 'Computer and Information Sciences',
    category: 'Information Technology',
    degreeLevel: "Master's",
    programId: 67036,
  },
  {
    name: 'Cybersecurity',
    category: 'Information Technology',
    degreeLevel: "Master's",
    programId: 67043,
  },
  {
    name: 'Data Science',
    category: 'Information Technology',
    degreeLevel: "Master's",
    programId: 67037,
  },
  {
    name: 'Human Resource Management in People Analytics',
    category: 'Business',
    degreeLevel: "Master's",
    programId: 67026,
  },
  {
    name: 'Kinesiology – Sport Management',
    category: 'Health & Kinesiology',
    degreeLevel: "Master's",
    programId: 67027,
  },
  {
    name: 'Leadership',
    category: 'Business',
    degreeLevel: "Master's",
    programId: 67035,
  },
  {
    name: 'Learning, Technology, and Design',
    category: 'Education',
    degreeLevel: "Master's",
    programId: 67025,
  },
  {
    name: 'Nursing – Family Nurse Practitioner',
    category: 'Nursing',
    degreeLevel: "Master's",
    programId: 67038,
  },
  {
    name: 'Nursing – Nurse Education',
    category: 'Nursing',
    degreeLevel: "Master's",
    programId: 67046,
  },
  {
    name: 'Nursing – Nurse Leadership',
    category: 'Nursing',
    degreeLevel: "Master's",
    programId: 67047,
  },
  {
    name: 'Nursing – Pediatric Nurse Practitioner',
    category: 'Nursing',
    degreeLevel: "Master's",
    programId: 67049,
  },
  {
    name: 'Nursing – RN to MSN',
    category: 'Nursing',
    degreeLevel: "Master's",
    programId: 67050,
  },
  // Doctoral
  {
    name: 'Executive Educational Leadership (Ed.D.)',
    category: 'Education',
    degreeLevel: 'Doctoral',
    programId: 67029,
  },
  {
    name: 'Mental Health and Human Services (Ed.D.)',
    category: 'Education',
    degreeLevel: 'Doctoral',
    programId: 67030,
  },
  {
    name: 'Educational Leadership (Ed.S.)',
    category: 'Education',
    degreeLevel: 'Doctoral',
    programId: 67024,
  },
  // Bachelor's
  {
    name: 'Biblical Studies',
    category: 'Religious Studies',
    degreeLevel: "Bachelor's",
    programId: 67002,
  },
  {
    name: 'Criminal Justice',
    category: 'Criminal Justice',
    degreeLevel: "Bachelor's",
    programId: 67003,
  },
  {
    name: 'Interdisciplinary Studies – Business Track',
    category: 'General Studies',
    degreeLevel: "Bachelor's",
    programId: 67004,
  },
  {
    name: 'Interdisciplinary Studies – Math and Science Track',
    category: 'General Studies',
    degreeLevel: "Bachelor's",
    programId: 67034,
  },
  {
    name: 'Interdisciplinary Studies – Humanities Track',
    category: 'General Studies',
    degreeLevel: "Bachelor's",
    programId: 67033,
  },
  {
    name: 'Interdisciplinary Studies',
    category: 'General Studies',
    degreeLevel: "Bachelor's",
    programId: 67051,
  },
  {
    name: 'Psychology – Christian Counseling',
    category: 'Psychology & Counseling',
    degreeLevel: "Bachelor's",
    programId: 67006,
  },
  {
    name: 'Psychology – Marriage and Family Studies',
    category: 'Psychology & Counseling',
    degreeLevel: "Bachelor's",
    programId: 67007,
  },
  {
    name: 'Psychology',
    category: 'Psychology & Counseling',
    degreeLevel: "Bachelor's",
    programId: 67008,
  },
  {
    name: 'Psychology to M.A. Psychology (5-Year Accelerated)',
    category: 'Psychology & Counseling',
    degreeLevel: "Bachelor's",
    programId: 67048,
  },
  {
    name: 'Theological Studies',
    category: 'Religious Studies',
    degreeLevel: "Bachelor's",
    programId: 67009,
  },
  {
    name: 'Business Administration',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 67005,
  },
  {
    name: 'Business Data Analytics',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 67052,
  },
  {
    name: 'Human Resource Development and Change',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 67053,
  },
  {
    name: 'International Business',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 67031,
  },
  {
    name: 'Management Information Systems',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 67054,
  },
  {
    name: 'Management',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 67001,
  },
  {
    name: 'Marketing',
    category: 'Business',
    degreeLevel: "Bachelor's",
    programId: 67032,
  },
]

const UMA_PROGRAMS: UniversityProgram[] = [
  {
    name: 'Associate of Science (A.S.) in Healthcare Accounting',
    category: 'Healthcare',
    degreeLevel: "Associate's",
    mProgramValue: 'Associate of Science (A.S.) in Healthcare Accounting',
  },
  {
    name: 'Associate of Science (A.S.) in Healthcare Management',
    category: 'Healthcare',
    degreeLevel: "Associate's",
    mProgramValue: 'Associate of Science (A.S.) in Healthcare Management',
  },
  {
    name: 'Associate of Science (A.S.) in Health & Human Services',
    category: 'Healthcare',
    degreeLevel: "Associate's",
    mProgramValue: 'Associate of Science (A.S.) in Health & Human Services',
  },
  {
    name: 'Associate of Science (A.S.) in Health Information Management',
    category: 'Healthcare',
    degreeLevel: "Associate's",
    mProgramValue:
      'Associate of Science (A.S.) in Health Information Management',
  },
  {
    name: 'Associate of Science (A.S.) in Medical Administrative Assistant',
    category: 'Healthcare',
    degreeLevel: "Associate's",
    mProgramValue:
      'Associate of Science (A.S.) in Medical Administrative Assistant',
  },
  {
    name: 'Associate of Science (A.S.) in Medical Billing & Coding',
    category: 'Healthcare',
    degreeLevel: "Associate's",
    mProgramValue: 'Associate of Science (A.S.) in Medical Billing & Coding',
  },
  {
    name: 'Associate of Science (A.S.) in Medical Office & Billing Specialist',
    category: 'Healthcare',
    degreeLevel: "Associate's",
    mProgramValue:
      'Associate of Science (A.S.) in Medical Office & Billing Specialist',
  },
]

export const universities: Record<string, UniversityData> = {
  'houston-christian-university': {
    slug: 'houston-christian-university',
    name: 'Houston Christian University',
    shortName: 'HCU',
    tagline:
      'Choose From a Wide Range Of Online Degrees at Houston Christian University - Learn More!',
    heroTagline:
      'Choose From a Wide Range Of Online Degrees at Houston Christian University - Learn More!',
    mission:
      'HCU offers students a higher education. By combining faith, reason and liberal arts into one core curriculum, we provide an education that’s based not only on technical training, but the teachings of Christ, reason, and the past successes and failures of humanity. It’s an education that puts our students on the fast track to being leaders, both in their careers and in their communities.',
    primaryColor: '#1f3a7a',
    secondaryColor: '#13245c',
    ctaColor: '#0E63C4',
    logoText: 'Houston Christian University',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/5SMgnbKMYhmby3oEtZyH6e/67119a3a74d41da2543935f5_Houston_Christian_University_nav.png',
    accreditation: 'SACSCOC Accredited',
    founded: '1960',
    type: 'Private Christian University',
    degreeLevels: ["Bachelor's", "Master's", 'Doctoral'],
    programs: HCU_PROGRAMS,
    features: [],
    tcpaText:
      'By submitting this form, I confirm that the information provided on this form is accurate and complete. I agree that Houston Christian University and its affiliates may call and/or text me about its offerings using automated technology. I understand consent is not a condition of purchasing services.',
    campusZipLookup: () => ({ ...ONLINE_CAMPUS }),
    programsForCampus: () => HCU_PROGRAMS.map((p) => ({ ...p })),
    landingContent: {
      headline: 'Online Degrees at Houston Christian University',
      subheadline: '',
      heroHighlights: [
        'Integrates faith and reason in academics',
        'Courses available at any time of the day',
        '60-year academic tradition focused on quality',
        'Some of the lowest tuition available today',
        'SACSCOC Accredited',
      ],
      features: [
        {
          icon: 'AwardIcon',
          title: 'High-quality Education',
          description:
            'HCU has a 60-year academic tradition focused on faith and quality of learning. With our accreditation, you’re getting a quality education recognized nationwide.',
        },
        {
          icon: 'LaptopIcon',
          title: 'Accessible',
          description:
            'Your courses are at any time of the day so that you may complete coursework when it is convenient for you. HCU Online is an ideal option for students looking to attend school on their own schedule.',
        },
        {
          icon: 'BrainIcon',
          title: 'Holistic Education',
          description:
            'HCU integrates faith and reason in its academics so you are prepared not only for your career, but for a meaningful life in service to God.',
        },
        {
          icon: 'GraduationCapIcon',
          title: 'Affordable',
          description:
            'HCU has some of the lowest tuition available today. We want to provide a convenient opportunity to earn your degree, but also desire to keep our tuition among the lowest in the nation.',
        },
      ],
      stats: [
        { value: '1960', label: 'Founded' },
        { value: '24/7', label: 'Online access' },
        { value: 'SACSCOC', label: 'Accredited' },
        { value: '50+', label: 'Online Programs' },
      ],
      testimonial: {
        quote:
          'The online program doesn’t place any limits on you. HCU gives you everything you need to succeed. All of my professors have been wonderful. HCU is like family, and the faith component is important to me. I absolutely cannot say enough wonderful things about HCU.',
        name: 'Faith Wilson',
        program: 'HCU Student',
      },
      infoSections: [
        {
          eyebrow: 'Why Choose HCU',
          heading: 'A flexible online experience built around your life.',
          body: 'HCU makes it easier to finish what you started. Our in-demand online degrees prepare you for the most sought-after career paths. Don’t just earn degrees—build the foundation for a meaningful, successful career guided by purpose and faith-informed values.',
          bullets: [
            'Integrates faith and reason in academics',
            'Courses available at any time of the day',
            '60-year academic tradition focused on quality',
            'Some of the lowest tuition available today',
          ],
        },
        {
          eyebrow: 'Accreditation = Quality Education',
          heading: 'Accreditation that means a quality education.',
          body: 'HCU maintains institutional accreditation (SACSCOC) and several programmatic accreditations, which means it consistently meets quality standards that all students deserve. You can rest assured knowing you are receiving the most for your investment at HCU.',
        },
        {
          eyebrow: 'Make an Investment in You',
          heading:
            'An investment that can pay dividends for the rest of your life.',
          body: 'HCU understands that earning your online degree is also a financial investment, and understanding your college tuition costs and options to pay for school is important. HCU has some of the lowest tuition available today. We want to provide a convenient opportunity to earn your degree, but also desire to keep our tuition among the lowest in the nation.',
        },
      ],
      finalCta: {
        headline: 'Interested in learning more about our degrees?',
        body: 'Take the next step toward a degree designed around your life. Tell us a little about you and an HCU advisor will reach out with program details and next steps.',
        buttonLabel: 'Get Started Now',
      },
      campusList: [{ id: 1, name: 'Online', programs: [] }],
    },
  },
  'crestmont-university': {
    slug: 'crestmont-university',
    name: 'Crestmont University',
    shortName: 'Crestmont',
    tagline: 'Education tailored to your life.',
    heroTagline: 'Education tailored to your life.',
    mission:
      'To provide high-quality, accessible, and innovative educational programs.',
    primaryColor: '#00426b',
    secondaryColor: '#Fcc23f',
    ctaColor: '#fcc23f',
    logoText: 'Crestmont University',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/cA6YdkTjQLKasPhVmij2X5/CU_Primary_logo_1_RGB_1.png',
    accreditation: 'Accredited',
    founded: '2020',
    type: 'Private',
    degreeLevels: ["Associate's", "Bachelor's"],
    programs: CRESTMONT_PROGRAMS,
    features: [],
    tcpaText:
      'By submitting this form, you are giving your express written consent to be called by or on behalf of Crestmont University regarding our educational programs and services using email, telephone or text-including our use of an automatic telephone dialing system for calls or texts to any wireless number provided. This consent is not required to purchase services or enroll in any programs.',
    // SJVC-style campus flow, but every zip resolves to "Online Campus".
    campusZipLookup: () => ({ ...ONLINE_CAMPUS }),
    programsForCampus: () => CRESTMONT_PROGRAMS.map((p) => ({ ...p })),
    landingContent: {
      headline: 'Education tailored to your life.',
      subheadline:
        'Crestmont University delivers high-quality, accessible, and innovative online degree programs designed around your life.',
      heroHighlights: [
        'One course at a time in an accelerated 5-, 6-, or 9-week format',
        'Access the online classroom 24/7 to study on your schedule',
        'Learn from expert faculty with real-world experience',
        'No standardized test scores required to enroll',
        'A three-week trial period to see if Crestmont is right for you',
      ],
      features: [
        {
          icon: 'AwardIcon',
          title: 'Transfer Your Credits',
          description:
            'Transfer approved college credits toward your bachelor’s program and earn credit for relevant work and life experience.',
        },
        {
          icon: 'LaptopIcon',
          title: 'One Course at a Time',
          description:
            'Focus on a single class in an accelerated format — 5 weeks for undergraduate, 6 for graduate, and 9 for doctoral dissertation courses.',
        },
        {
          icon: 'BrainIcon',
          title: 'Study 24/7',
          description:
            'Access the online classroom around the clock so you can study at night, on the weekends, or during your lunch break.',
        },
        {
          icon: 'GraduationCapIcon',
          title: 'Learn From Experts',
          description:
            'Study with expert faculty who bring real-world experience in their respective fields, and join the Honors Program for high-achieving bachelor’s students.',
        },
      ],
      stats: [
        { value: '3 wks', label: 'Trial period' },
        { value: '5 wk', label: 'Accelerated courses' },
        { value: '24/7', label: 'Online classroom' },
        { value: 'WSCUC', label: 'Accredited' },
      ],
      infoSections: [
        {
          eyebrow: 'Why Choose Crestmont',
          heading: 'A flexible online experience built around your life.',
          body: 'Crestmont makes it easier to finish what you started. Standardized test scores aren’t required for enrollment, and a three-week trial period helps you decide whether Crestmont is right for you.',
          bullets: [
            'Transfer approved college credits toward your bachelor’s program',
            'Earn college credit for relevant work and life experience through Prior Learning Assessment',
            'Use a three-week trial period to see if Crestmont is right for you',
            'Take one course at a time in an accelerated 5-, 6-, or 9-week format',
            'Access the online classroom 24/7 to study on your own schedule',
            'Learn from expert faculty with real-world experience in their fields',
          ],
        },
        {
          eyebrow: 'Accreditation = Quality Education',
          heading: 'Accreditation that means a quality education.',
          body: 'Crestmont maintains institutional accreditation and several programmatic accreditations, which means it consistently meets quality standards that all students deserve. Whether you are transferring credits you have earned from another institution or just starting your college journey, you can rest assured knowing you are receiving the most for your investment at Crestmont.',
        },
        {
          eyebrow: 'Make an Investment in You',
          heading:
            'An investment that can pay dividends for the rest of your life.',
          body: 'Crestmont understands that earning your online degree is also a financial investment, and understanding your college tuition costs and options to pay for school is important. Your tuition and fees depend on your degree level and field of study. Military students or students with benefits through their employer may qualify for reduced tuition rates or fee waivers, and Crestmont offers many ways to save through grants and scholarship aid.',
        },
        {
          eyebrow: 'The Crestmont Promise',
          heading:
            'Explore your first course over three weeks and find the program that fits.',
          body: 'The Crestmont Promise gives new degree-seeking students the opportunity to experience the Crestmont online platform before enrolling and decide whether it is the right fit.',
        },
      ],
      finalCta: {
        headline: 'Interested in learning more about our degrees?',
        body: 'Take the next step toward a degree designed around your life. Tell us a little about you and a Crestmont advisor will reach out with program details and next steps.',
        buttonLabel: 'Get Started Now',
      },
      campusList: [{ id: 1, name: 'Online', programs: [] }],
    },
  },
  'colorado-technical-university': {
    slug: 'colorado-technical-university',
    name: 'Colorado Technical University',
    shortName: 'CTU',
    tagline: 'Pursue a degree without putting your life on hold',
    heroTagline: 'Pursue a degree without putting your life on hold',
    mission:
      'Our mission is to provide industry-relevant higher education to a diverse student population through innovative technology and experienced faculty, enabling the pursuit of personal and professional goals.',
    primaryColor: '#991B1B',
    secondaryColor: '#1a1a1a',
    ctaColor: '#B91C1C',
    logoText: 'Colorado Technical University',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/913DY1joyKbGCcsqnLc42e/CTU_Logo.svg',
    heroImageUrl:
      'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1400&h=800&fit=crop&q=80',
    phoneNumber: '855-204-0444',
    classesStartDate: 'May 5, 2026',
    degreeLevels: ["Associate's", "Bachelor's", "Master's", 'Doctoral'],
    programs: CTU_PROGRAMS,
    features: [
      {
        icon: 'ArrowLeftRightIcon',
        label: 'Transfer Credits',
        description: 'Transfer in your eligible credits',
        superscript: '1',
      },
      {
        icon: 'FileCheckIcon',
        label: 'Financial Aid',
        description: 'Potential funding options available',
      },
      {
        icon: 'TimerIcon',
        label: 'CTU FastTrack™',
        description: 'Earn college credit for what you already know',
        superscript: '2',
      },
      {
        icon: 'AwardIcon',
        label: 'Academic Excellence',
        description: 'Study with instructors who are experienced professionals',
      },
    ],
    testimonial: {
      quote:
        'I had an awesome journey here at CTU. I chose CTU due the convenience of on line classes. This was such an amazing program. The professors were amazing and were all the steps of the way. I also loved my Success coach who was always available! CTU has the best online programs and was great experience!',
      name: 'Catherine J.',
      program: 'Master of Science in Healthcare Management 2024',
    },
    mobileSection: {
      heading: 'Access Classes from Your Phone',
      description:
        "CTU embraces new technologies that innovate and enhance the learning experience by offering increased connectivity and the freedom to choose where, when and how you study. That's why we are proud to bring you CTU Mobile, a unique educational experience in the palm of your hand. With our state-of-the-art mobile app, you can access your courses anywhere and work toward achieving your academic goals on your own time.",
    },
    accreditation: 'HLC Accredited',
    founded: '1965',
    type: 'Private, For-Profit',
    tcpaText:
      'By checking this box, I consent to receive calls and text messages from Colorado Technical University (CTU) at the number provided above regarding educational opportunities. I understand that these calls may be generated using an automated technology and that my consent is not required to enroll. Message and data rates may apply.',
    // SJVC-style campus flow, but every zip resolves to "Online Campus".
    campusZipLookup: () => ({ ...ONLINE_CAMPUS }),
    programsForCampus: () => CTU_PROGRAMS.map((p) => ({ ...p })),
    landingContent: {
      headline: 'Pursue a degree without putting your life on hold',
      subheadline:
        'Colorado Technical University offers flexible, industry-relevant online programs designed to fit your schedule and help you reach your personal and professional goals.',
      features: [],
      campusList: [{ id: 1, name: 'Online', programs: [] }],
    },
  },
  'university-of-arizona-global-campus': {
    slug: 'university-of-arizona-global-campus',
    name: 'University of Arizona Global Campus',
    shortName: 'UAGC',
    tagline: 'Education tailored to your life.',
    heroTagline: 'Education tailored to your life.',
    mission:
      'To provide high-quality, accessible, and innovative educational programs.',
    primaryColor: '#1B3A5C',
    secondaryColor: '#0C234B',
    ctaColor: '#CC0033',
    logoText: 'University of Arizona Global Campus',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/qC5uLXKVWC3G3tGtUcsB16/University_of_Arizona_Global_Campus_primary_logo.png',
    accreditation: 'WSCUC Accredited',
    founded: '2020',
    type: 'Public',
    degreeLevels: ["Associate's", "Bachelor's"],
    programs: UAGC_PROGRAMS,
    features: [],
    tcpaText:
      'By checking this box, I consent to receive calls and text messages from the University of Arizona Global Campus (UAGC) and its representatives at the number provided above regarding educational opportunities. I understand that these calls may be generated using an automated technology and that my consent is not required to enroll. Message and data rates may apply. I may withdraw my consent at any time.',
    // SJVC-style campus flow, but every zip resolves to "Online Campus".
    campusZipLookup: () => ({ ...ONLINE_CAMPUS }),
    programsForCampus: () => UAGC_PROGRAMS.map((p) => ({ ...p })),
    landingContent: {
      headline: 'Education tailored to your life.',
      subheadline:
        'University of Arizona Global Campus delivers high-quality, accessible, and innovative online degree programs designed around your life.',
      heroHighlights: [
        'One course at a time in an accelerated 5-, 6-, or 9-week format',
        'Access the online classroom 24/7 to study on your schedule',
        'Learn from expert faculty with real-world experience',
        'No standardized test scores required to enroll',
        'A three-week trial period to see if UAGC is right for you',
      ],
      features: [
        {
          icon: 'AwardIcon',
          title: 'Transfer Your Credits',
          description:
            'Transfer approved college credits toward your bachelor’s program and earn credit for relevant work and life experience.',
        },
        {
          icon: 'LaptopIcon',
          title: 'One Course at a Time',
          description:
            'Focus on a single class in an accelerated format — 5 weeks for undergraduate, 6 for graduate, and 9 for doctoral dissertation courses.',
        },
        {
          icon: 'BrainIcon',
          title: 'Study 24/7',
          description:
            'Access the online classroom around the clock so you can study at night, on the weekends, or during your lunch break.',
        },
        {
          icon: 'GraduationCapIcon',
          title: 'Learn From Experts',
          description:
            'Study with expert faculty who bring real-world experience in their respective fields, and join the Honors Program for high-achieving bachelor’s students.',
        },
      ],
      stats: [
        { value: '3 wks', label: 'Trial period' },
        { value: '5 wk', label: 'Accelerated courses' },
        { value: '24/7', label: 'Online classroom' },
        { value: 'WSCUC', label: 'Accredited' },
      ],
      testimonial: {
        quote:
          'Life has totally changed because there were so many things that came out of this experience for me.',
        name: 'Jessica Smith',
        program: 'UAGC Student',
      },
      infoSections: [
        {
          eyebrow: 'Why Choose UAGC',
          heading: 'A flexible online experience built around your life.',
          body: 'UAGC makes it easier to finish what you started. Standardized test scores aren’t required for enrollment, and a three-week trial period helps you decide whether UAGC is right for you.',
          bullets: [
            'Transfer approved college credits toward your bachelor’s program',
            'Earn college credit for relevant work and life experience through Prior Learning Assessment',
            'Use a three-week trial period to see if UAGC is right for you',
            'Take one course at a time in an accelerated 5-, 6-, or 9-week format',
            'Access the online classroom 24/7 to study on your own schedule',
            'Learn from expert faculty with real-world experience in their fields',
          ],
        },
        {
          eyebrow: 'Accreditation = Quality Education',
          heading: 'Accreditation that means a quality education.',
          body: 'UAGC maintains institutional accreditation and several programmatic accreditations, which means it consistently meets quality standards that all students deserve. Whether you are transferring credits you have earned from another institution or just starting your college journey, you can rest assured knowing you are receiving the most for your investment at UAGC.',
        },
        {
          eyebrow: 'Make an Investment in You',
          heading:
            'An investment that can pay dividends for the rest of your life.',
          body: 'UAGC understands that earning your online degree is also a financial investment, and understanding your college tuition costs and options to pay for school is important. Your tuition and fees depend on your degree level and field of study. Military students or students with benefits through their employer may qualify for reduced tuition rates or fee waivers, and UAGC offers many ways to save through grants and scholarship aid.',
        },
        {
          eyebrow: 'The UAGC Promise',
          heading:
            'Explore your first course over three weeks and find the program that fits.',
          body: 'The UAGC Promise gives new degree-seeking students the opportunity to experience the UAGC online platform before enrolling and decide whether it is the right fit.',
        },
      ],
      finalCta: {
        headline: 'Interested in learning more about our degrees?',
        body: 'Take the next step toward a degree designed around your life. Tell us a little about you and a UAGC advisor will reach out with program details and next steps.',
        buttonLabel: 'Get Started Now',
      },
      campusList: [{ id: 1, name: 'Online', programs: [] }],
    },
  },
  'san-joaquin-valley-college': {
    slug: 'san-joaquin-valley-college',
    name: 'San Joaquin Valley College',
    shortName: 'SJVC',
    tagline: 'A Private Career College',
    heroTagline: 'A Private Career College',
    mission:
      'San Joaquin Valley College prepares students for skilled, hands-on careers through focused, accelerated diploma and degree programs.',
    primaryColor: '#1E78C2',
    secondaryColor: '#0F4F8B',
    ctaColor: '#22A55C',
    logoText: 'San Joaquin Valley College',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/h4UizCs35BQrDroCjKSA7H/SJVC_logo.svg',
    accreditation: 'ACCSC Accredited',
    founded: '1977',
    type: 'Private Career College',
    degreeLevels: ['Diploma'],
    programs: [
      {
        name: 'Electrical Technology',
        category: 'Skilled Trades',
        degreeLevel: 'Diploma',
        slug: 'electrical-technology',
      },
      {
        name: 'Heating, Ventilation, AC and Refrigeration',
        category: 'Skilled Trades',
        degreeLevel: 'Diploma',
        slug: 'hvac',
      },
      {
        name: 'Maintenance Technician',
        category: 'Skilled Trades',
        degreeLevel: 'Diploma',
        slug: 'maintenance-technician',
      },
      {
        name: 'Construction Management',
        category: 'Skilled Trades',
        degreeLevel: 'Diploma',
        slug: 'construction-management',
      },
    ],
    features: [],
    heroBackgroundImage:
      'https://cdn.magicpatterns.com/uploads/2974HoVszAU37LFnoFezRY/ChatGPT_Image_May_5,_2026,_03_56_38_PM.png',
    tcpaTextSms: 'Allow text messages from San Joaquin Valley College.',
    tcpaText:
      'By submitting this form, I expressly authorize San Joaquin Valley College, its subsidiaries, affiliates, agents and contractors to contact me regarding educational services via email, telephone (including cellular) or text message using automated technology at the email address and phone numbers provided. I understand this consent is not required to attend San Joaquin Valley College. I understand message and data rates may apply, and that my consent may be revoked at any time by notifying San Joaquin Valley College. View our privacy policy. Visit https://www.sjvc.edu/admissions/consumer-information/ for important information on program outcomes.',
    campusZipLookup: (zip: string) => {
      const campus = _sjvcGetCampusByZip(zip)
      return campus ? { id: campus.id, name: campus.name } : null
    },
    programsForCampus: (campusId: number) =>
      _sjvcGetProgramsForCampus(campusId),
    landingContent: {
      headline: 'Train for a Hands-On Career You Can Be Proud Of',
      subheadline:
        'San Joaquin Valley College helps motivated students gain real-world skills through focused, accelerated programs taught by experienced industry professionals — so you can graduate ready to work.',
      features: [
        {
          icon: 'BrainIcon',
          title: 'Real-World Skills',
          description:
            'Hands-on training and industry-experienced instructors prepare you for the work you want to do.',
        },
        {
          icon: 'AwardIcon',
          title: 'Start Your Path',
          description:
            'Choose from diploma, certificate, and associate degree programs designed to get you career-ready.',
        },
        {
          icon: 'LaptopIcon',
          title: 'Flexible Learning',
          description:
            'Learn on a schedule that works for your life with day, evening, and online program options.',
        },
        {
          icon: 'DollarSignIcon',
          title: 'Affordable Tuition',
          description:
            'Financial aid is available for those who qualify. Tuition varies by program and campus.',
        },
      ],
      careerSection: {
        headline: 'Preparing for a New Career. Anytime, Anywhere.',
        body: "We know you have what it takes. That's why we're here to help you reach your full potential. SJVC offers programs that can prepare you for a diverse range of professional careers — without putting your life on hold.",
        imageUrl:
          'https://cdn.magicpatterns.com/uploads/v7tdNYfNE9U241JHrKMNap/SJVC_-_lower_image.webp',
        imageAlt: 'SJVC Electrical Technology student with tools',
      },
      communityImage: {
        url: 'https://cdn.magicpatterns.com/uploads/vVg3Z16qLb1rRZSB5BHdwH/Certificate-and-Associate-Degree-Programs-Hero.webp',
        alt: 'SJVC students from Electrical Technology and Medical programs',
        eyebrow: 'Join the SJVC Community',
        heading: 'A community built for students who want more.',
        body: "From day one, SJVC students are surrounded by instructors, advisors, and classmates who are invested in their success. Whether you're heading into healthcare, the trades, or business, you'll graduate with the skills, confidence, and connections to thrive.",
      },
      campusList: [
        {
          id: 33,
          name: 'Bakersfield',
          programs: ['Electrical Technology', 'HVAC & Refrigeration'],
        },
        {
          id: 36,
          name: 'Fresno TEC',
          programs: ['Electrical Technology', 'HVAC & Refrigeration'],
        },
        {
          id: 38,
          name: 'Hesperia',
          programs: ['Electrical Technology', 'HVAC & Refrigeration'],
        },
        { id: 39, name: 'Lancaster', programs: ['HVAC & Refrigeration'] },
        {
          id: 41,
          name: 'Modesto',
          programs: ['Electrical Technology', 'Maintenance Technician'],
        },
        {
          id: 43,
          name: 'Ontario',
          programs: [
            'Electrical Technology',
            'HVAC & Refrigeration',
            'Maintenance Technician',
            'Construction Management',
          ],
        },
        {
          id: 45,
          name: 'Rancho Mirage',
          programs: ['Electrical Technology', 'HVAC & Refrigeration'],
        },
        { id: 46, name: 'Santa Maria', programs: [] },
        {
          id: 47,
          name: 'Temecula',
          programs: ['Electrical Technology', 'HVAC & Refrigeration'],
        },
        {
          id: 48,
          name: 'Visalia',
          programs: [
            'Electrical Technology',
            'HVAC & Refrigeration',
            'Maintenance Technician',
          ],
        },
        { id: 220, name: 'San Leandro', programs: ['Electrical Technology'] },
        {
          id: 222,
          name: 'Phoenix TEC',
          programs: ['Electrical Technology', 'HVAC & Refrigeration'],
        },
        { id: 223, name: 'Sacramento', programs: ['Electrical Technology'] },
      ],
      stats: [
        { value: '1977', label: 'Educating since' },
        { value: '13', label: 'Campus locations' },
        { value: '50+', label: 'Career-focused programs' },
        { value: 'ACCSC', label: 'Nationally accredited' },
      ],
      testimonial: {
        quote:
          'SJVC gave me real, hands-on training and instructors who actually cared. I went from unsure about my future to confidently working in my field within months of graduating.',
        name: 'Maria G.',
        program: 'SJVC Graduate',
      },
      infoSections: [
        {
          eyebrow: 'How SJVC Works',
          heading:
            'A focused path designed around your career — not the other way around.',
          body: "SJVC programs are built around the skills employers actually look for. You'll learn from instructors with real industry experience and graduate with the practical know-how to step into your new role on day one.",
          bullets: [
            'Accelerated diploma, certificate, and associate degree options',
            'Hands-on labs and equipment that mirror real workplaces',
            'Dedicated career services to help you land your first job',
            'Day, evening, and online schedules to fit your life',
          ],
        },
        {
          eyebrow: 'Accreditation & Outcomes',
          heading: 'A trusted name in career education for nearly 50 years.',
          body: 'San Joaquin Valley College is institutionally accredited by the Accrediting Commission of Career Schools and Colleges (ACCSC) and has helped tens of thousands of students launch new careers. Visit sjvc.edu/admissions/consumer-information for important information on program outcomes.',
          bullets: [
            'ACCSC-accredited institution',
            'Financial aid available for those who qualify',
            'Career-services support before and after graduation',
            'Programs aligned with regional employer needs',
          ],
        },
      ],
      finalCta: {
        headline: 'Ready to take the next step?',
        body: 'Tell us a little about you and a friendly SJVC admissions advisor will reach out with the program details and next steps.',
        buttonLabel: 'Request Information',
      },
    },
  },
  'carrington-college': {
    slug: 'carrington-college',
    name: 'Carrington College',
    shortName: 'Carrington',
    tagline: 'Career-focused healthcare education',
    heroTagline: 'Career-focused healthcare education',
    mission:
      'Carrington College helps motivated students gain real-world skills through hands-on healthcare and career programs taught by experienced industry professionals.',
    primaryColor: '#1E3A6F',
    secondaryColor: '#15294F',
    ctaColor: '#1E3A6F',
    logoText: 'Carrington College',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/3wXCmBZEYmszo4mCNS3nbM/image_26_(1).png',
    accreditation: 'ACCSC Accredited',
    founded: '1967',
    type: 'Private Career College',
    degreeLevels: ['Diploma', 'Undergraduate'],
    programs: _carringtonPrograms.map((p) => ({
      name: p.name,
      category: p.category,
      degreeLevel: p.degreeLevel,
      slug: p.slug,
    })),
    features: [],
    heroBackgroundImage:
      'https://cdn.magicpatterns.com/uploads/njz4YLzzbXZLg4BaPf9equ/3e226cfb-0665-43b5-891f-bbb6f803acf3.png',
    tcpaTextSms: 'Allow for text messages from Carrington College.',
    tcpaText:
      'By submitting this form, I expressly authorize Carrington College, and its parent company San Joaquin Valley College, Inc., their subsidiaries, affiliates, agents, and contractors to contact me regarding educational services via email, telephone (including cellular) or text message – using automated technology – at the email address and phone numbers provided. I understand this consent is not required to attend the college. I understand message and data rates may apply, and that my consent may be revoked at any time by notifying the college. View our privacy policy. Visit https://carrington.edu/admissions/student-consumer-information/ for important information on program outcomes.',
    campusZipLookup: (zip: string) => {
      const campus = _carringtonGetCampusByZip(zip)
      return campus ? { id: campus.id, name: campus.name } : null
    },
    programsForCampus: (campusId: number) =>
      _carringtonGetProgramsForCampus(campusId),
    landingContent: {
      headline: 'Train for a Healthcare Career You Can Be Proud Of',
      subheadline:
        'Carrington College helps motivated students earn their certificate or degree in as few as 9 months — with hands-on training, industry-experienced instructors, and 27 campus locations across the Western U.S.',
      features: [
        {
          icon: 'AwardIcon',
          title: 'Hands-On Classes',
          description:
            'Qualified instructors lead hands-on classes to give you real-world experience.',
        },
        {
          icon: 'GraduationCapIcon',
          title: 'Convenient Locations',
          description:
            'Choose from online courses or study in person at one of our 27 campus locations across the Western U.S.',
        },
        {
          icon: 'LaptopIcon',
          title: 'Career Services',
          description:
            'From career planning to job placement, focused on helping you succeed.',
        },
        {
          icon: 'DollarSignIcon',
          title: 'Fast Track Learning',
          description:
            'Carrington College graduates earn their certificate in as few as 9 months.',
        },
      ],
      careerSection: {
        headline: 'Online and On-Campus Programs are Open for Enrollment',
        body: 'Why Wait… Make the First Step to a Fresh Start Today! Talk to an enrollment services representative about your options and earn your certificate in as few as 9–15 months.',
        imageUrl:
          'https://cdn.magicpatterns.com/uploads/rbjm4i8WSqPhF7HWW1drgE/ChatGPT_Image_May_6,_2026,_12_04_25_PM.png',
        imageAlt:
          'Carrington College student in scrubs holding books in a clinical classroom',
      },
      communityImage: {
        url: 'https://cdn.magicpatterns.com/uploads/x9njmQHgCyT3F5wuX1xydw/Carrington-College-HP-Hero-2026.webp',
        alt: 'Carrington College students in scrubs',
        eyebrow: 'Join the Carrington Community',
        heading: 'A community built for healthcare careers.',
        body: "From day one, Carrington students are surrounded by instructors, advisors, and classmates invested in their success. Whether you're heading into nursing, medical assisting, dental, or veterinary fields, you'll graduate with the skills, confidence, and connections to thrive.",
      },
      stats: [
        { value: '1967', label: 'Educating since' },
        { value: '27', label: 'Campus locations' },
        { value: '9 mo', label: 'Programs as fast as' },
        { value: 'ACCSC', label: 'Nationally accredited' },
      ],
      testimonial: {
        quote:
          'Carrington gave me real, hands-on training and instructors who actually cared. I went from unsure about my future to confidently working in my field within months of graduating.',
        name: 'Jasmine R.',
        program: 'Carrington College Graduate',
      },
      infoSections: [
        {
          eyebrow: 'How Carrington Works',
          heading:
            'A focused path designed around your healthcare career — not the other way around.',
          body: "Carrington programs are built around the skills employers actually look for. You'll learn from instructors with real industry experience and graduate with the practical know-how to step into your new role on day one.",
          bullets: [
            'Accelerated certificate and associate degree options',
            'Hands-on labs and equipment that mirror real clinical settings',
            'Dedicated career services to help you land your first job',
            'Online and on-campus schedules to fit your life',
          ],
        },
        {
          eyebrow: 'Accreditation & Outcomes',
          heading: 'A trusted name in career education for over 55 years.',
          body: 'Carrington College is institutionally accredited by the Accrediting Commission of Career Schools and Colleges (ACCSC) and has helped tens of thousands of students launch new careers in healthcare and beyond.',
          bullets: [
            'ACCSC-accredited institution',
            'Financial aid available for those who qualify',
            'Career-services support before and after graduation',
            '27 campus locations across the Western U.S.',
          ],
        },
      ],
      finalCta: {
        headline: 'Ready to take the next step?',
        body: 'Tell us a little about you and a friendly Carrington admissions advisor will reach out with program details and next steps.',
        buttonLabel: 'Talk to Us',
      },
      campusList: _carringtonGetCampusDirectory(),
    },
  },
  'ultimate-medical-academy': {
    slug: 'ultimate-medical-academy',
    name: 'Ultimate Medical Academy',
    shortName: 'UMA',
    tagline: 'Healthcare Career Training',
    heroTagline: 'Healthcare Career Training',
    mission:
      'Ultimate Medical Academy helps students build practical healthcare knowledge with flexible learning and support throughout their education journey.',
    primaryColor: '#176E9F',
    secondaryColor: '#0D4265',
    ctaColor: '#18D8C0',
    ctaTextColor: '#0D4265',
    logoText: 'Ultimate Medical Academy',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/njVGGmdgMtQHauw8NPMShT/UMA_LOGO_(1).webp',
    accreditation: 'Institutionally accredited by ABHES',
    founded: 'Healthcare-focused education',
    type: 'Private Career College',
    degreeLevels: ["Associate's"],
    programs: UMA_PROGRAMS,
    features: [],
    formHeading: 'Learn how we can help you succeed.',
    submitLabel: 'Request Info',
    singleProgramSelect: true,
    consentOnSubmit: true,
    tcpaText:
      'By clicking the Request Info button below, I am providing my eSIGN signature and express written consent for Ultimate Medical Academy (UMA), and parties calling on its behalf, to call or text me at the number provided above for purposes relating to educational opportunities with UMA, including through the use of automatic telephone dialing technology. I am authorized to consent to receive these communications at the phone number provided. I understand that I am consenting to receive calls and text messages regardless of whether the number provided is on any do not call list, either now or in the future. I acknowledge that my consent is not required to enroll, and I may revoke my consent at any time. I acknowledge that all calls may be recorded.',
    heroBackgroundImage:
      'https://cdn.magicpatterns.com/uploads/br5nxfXurTya7wJ6Dtqd98/Medical.png',
    landingContent: {
      headline: 'Healthcare Career Training',
      subheadline:
        'UMA students receive personalized support from the start of their education through career preparation and beyond.',
      heroHighlights: [
        'Healthcare-focused education designed around your goals',
        'Flexible online learning with individualized support',
        'Career services and job-search guidance for graduates',
      ],
      features: [
        {
          icon: 'DollarSignIcon',
          title: 'Financial Aid Guidance',
          description:
            'Explore financial aid options and get guidance through the application process.',
        },
        {
          icon: 'GraduationCapIcon',
          title: 'Career Services',
          description:
            'Build job-search skills, prepare for interviews, and explore career opportunities.',
        },
        {
          icon: 'LaptopIcon',
          title: 'Online Learning',
          description:
            'Learn through an engaging online environment with instructor and advisor support.',
        },
        {
          icon: 'AwardIcon',
          title: 'Accredited Education',
          description:
            'UMA is institutionally accredited by the Accrediting Bureau of Health Education Schools.',
        },
      ],
      stats: [
        { value: 'ABHES', label: 'Institutionally accredited' },
        { value: 'Online', label: 'Learning options' },
        { value: 'Career', label: 'Services support' },
        { value: 'UMA', label: 'Healthcare education' },
      ],
      careerSection: {
        headline: 'A healthcare education built for your next step.',
        body: 'Explore career-focused programs and connect with UMA to learn which path may fit your goals.',
        imageUrl:
          'https://cdn.magicpatterns.com/patterns/generated-images/e126a444-772c-4fc9-9a00-8eb445726510.jpg',
        imageAlt:
          'Healthcare graduate in scrubs celebrating with her daughter after graduation',
      },
      communityImage: {
        url: 'https://cdn.magicpatterns.com/patterns/generated-images/64276434-7055-46be-8826-8777092c510a.jpg',
        alt: 'Online learner studying from home with a laptop and notebook',
        eyebrow: 'Flexible Learning',
        heading: 'Learn in a way that works with your life.',
        body: 'Connect with instructors, advisors, and peers while working toward your healthcare career goals.',
      },
      testimonial: {
        quote:
          'The flexible learning format helped me keep moving toward my goals while balancing everything else in my life.',
        name: 'UMA Graduate',
        program: 'Healthcare Program',
      },
      infoSections: [
        {
          eyebrow: 'Why UMA',
          heading: 'Support that follows you through every stage of your journey.',
          body: 'From exploring program options to preparing for a career, UMA is designed to help you move forward with clarity and confidence.',
          bullets: [
            'Healthcare-focused diploma and associate degree options',
            'Individualized guidance from enrollment through graduation',
            'Career services and job-search preparation',
            'Financial aid guidance for those who qualify',
          ],
        },
        {
          eyebrow: 'Accreditation & Learning',
          heading: 'A quality-focused approach to healthcare education.',
          body: 'Ultimate Medical Academy is institutionally accredited by the Accrediting Bureau of Health Education Schools (ABHES). Coursework is delivered online, with on-site experiences required for select programs.',
          bullets: [
            'Institutionally accredited by ABHES',
            'Online learning designed for adult learners',
            'Program requirements vary by field of study',
            'Support from instructors and advisors',
          ],
        },
      ],
      finalCta: {
        headline: 'Ready to explore your healthcare education options?',
        body: 'Tell us about your goals and an UMA representative can share program details and next steps.',
        buttonLabel: 'Request Info',
      },
      campusList: [{ id: 1, name: 'Online', programs: [] }],
    },
  },
}

export const getUniversityBySlug = (slug: string) => universities[slug]
